import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { BrandSummary } from "@shared/types";
import { api } from "../lib/api";
import { SearchBox } from "../components/SearchBox";
import { BrandGrid } from "../components/BrandGrid";
import { EmptyState } from "../components/EmptyState";
import { isAbortError } from "../lib/asyncState";

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const [results, setResults] = useState<BrandSummary[]>([]);
  const [mode, setMode] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!q) { setResults([]); setMode(""); setError(null); return; }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api.search(q, { signal: controller.signal })
      .then((r) => { setResults(r.items); setMode(r.mode); })
      .catch((err) => {
        if (isAbortError(err)) return;
        setResults([]);
        setMode("");
        setError("Search is not available right now.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [q]);

  return (
    <div className="container" style={{ paddingBlock: "2rem" }}>
      <h2>Search</h2>
      <p style={{ color: "var(--fg-muted)", marginTop: "0.75rem", maxWidth: "55ch" }}>
        Try a phrase like <em>"outdoor but not sporty"</em> or <em>"low-logo Scandinavian menswear"</em>.
        Results blend semantic similarity with brand metadata.
      </p>
      <div style={{ marginTop: "1.5rem" }}>
        <SearchBox initial={q} />
      </div>
      {q && (
        <>
          <div style={{ marginTop: "2rem", color: "var(--fg-subtle)", display: "flex", justifyContent: "space-between" }}>
            <span>{loading ? "Searching…" : `${results.length} result${results.length === 1 ? "" : "s"} for “${q}”`}</span>
            {mode && <span style={{ fontSize: "0.78rem" }}>mode: {mode}</span>}
          </div>
          <div style={{ marginTop: "1rem" }}>
            {error ? (
              <EmptyState title="Search failed" actions={<button className="primary" onClick={() => window.location.reload()}>Try again</button>}>
                {error}
              </EmptyState>
            ) : (
              <BrandGrid brands={results} emptyMessage="No brands match that query yet." />
            )}
          </div>
        </>
      )}
    </div>
  );
}
